import { useEffect } from "react";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  AUTOCOMPLETE_REASON,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  SCHEDULE_OFFSET_REFERENCES_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { toKebabCase } from "@miplatform/utils";
import { MenuItem } from "@mui/material";
import { useAddAssignmentTemplateModal } from "./useAddAssignmentTemplateModal";

const initialData = {
  name: "",
};

export function InspectionTemplateAssignmentTemplateModal({
  item,
  open,
  getAllAssignmentTemplates,
  title,
  buttonTitle,
  onClose,
  loading,
  onSubmit = () => null,
}) {
  const {
    templates,
    findTemplate,
    getAssignmentTemplates: getLocalAssignmentTemplates,
  } = useAddAssignmentTemplateModal({
    getAllAssignmentTemplates,
  });

  useEffect(() => {
    if (open && !item) {
      getLocalAssignmentTemplates();
    }
  }, [open]);

  const edit = useEdit(item || initialData, []);

  const isExtraFieldsRequired = Boolean(edit.edits.autoSchedule);

  const fields = [
    {
      label: "Assignment Template",
      name: "name",
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      options: templates,
      disableClearable: true,
      renderOption: (props, { value, key }) => (
        <MenuItem
          {...props}
          key={key}
          data-testid={`template-option-${toKebabCase(value)}`}
        >
          {value}
        </MenuItem>
      ),
      fullWidth: true,
      width: "100%",
      required: true,
      readOnly: !!item,
      validationRule: z.string().min(1, "Template is required"),
      handleChange: (event, option) => {
        const value = option?.value || "";
        edit.update({ name: value });
      },
      onInputChange: (event, value, reason) => {
        if (reason === AUTOCOMPLETE_REASON.CLEAR) {
          edit.update({ name: "" });
        } else if (reason === AUTOCOMPLETE_REASON.INPUT) {
          edit.update({ name: value || "" });
        }
      },
    },
    {
      label: "Trigger InProgress Inspection Status",
      name: "triggersInProgressinspectionStatus",
      width: "100%",
      padding: 0,
      fieldType: INPUT_TYPES.CHECKBOX,
    },
    {
      label: "Trigger Complete Inspection Status",
      name: "triggersCompleteOrderStatus",
      width: "100%",
      padding: 0,
      fieldType: INPUT_TYPES.CHECKBOX,
    },
    {
      label: "Auto Schedule",
      name: "autoSchedule",
      width: "100%",
      padding: 0,
      fieldType: INPUT_TYPES.CHECKBOX,
      handleChange: ({ target: { checked } }) => {
        if (checked) {
          edit.update({ autoSchedule: checked });
          if (
            !edit.edits.scheduleOffsetReference ||
            !edit.edits.scheduleOffsetDays
          ) {
            edit.setError({
              ...(!edit.edits.scheduleOffsetReference && {
                scheduleOffsetReference: {
                  message: ["Schedule Reference Date is required"],
                },
              }),
              ...(!edit.edits.scheduleOffsetDays && {
                scheduleOffsetDays: {
                  message: ["Schedule Offset Days is required"],
                },
              }),
            });
          }
        } else {
          edit.update({
            scheduleOffsetDays: "",
            scheduleOffsetReference: "",
            autoSchedule: checked,
          });
          edit.deleteError(["scheduleOffsetDays", "scheduleOffsetReference"]);
        }
      },
    },
    {
      label: "Offset Reference Date",
      name: "scheduleOffsetReference",
      fieldType: INPUT_TYPES.SELECT,
      options: SCHEDULE_OFFSET_REFERENCES_OPTIONS,
      width: "100%",
      handleChange: ({ target: { value } }) => {
        edit.update({ scheduleOffsetReference: value, autoSchedule: true });
        if (!edit.edits.scheduleOffsetDays) {
          edit.setError(
            {
              scheduleOffsetDays: {
                message: ["Schedule Offset Days is required"],
              },
            },
            "scheduleOffsetReference"
          );
        } else {
          edit.deleteError("scheduleOffsetReference");
        }
      },
    },
    {
      label: "Schedule Offset Days",
      name: "scheduleOffsetDays",
      required: isExtraFieldsRequired,
      fieldType: INPUT_TYPES.NUMBER,
      handleChange: ({ target: { value } }) => {
        edit.update({
          scheduleOffsetDays: value,
          autoSchedule: true,
        });

        const isValueInvalid = !value;

        if (!edit.edits.scheduleOffsetReference || isValueInvalid) {
          edit.setError(
            {
              ...(!edit.edits.scheduleOffsetReference && {
                scheduleOffsetReference: {
                  message: ["Schedule Reference Date is required"],
                },
              }),
              ...(isValueInvalid && {
                scheduleOffsetDays: {
                  message: ["Schedule Offset Days is required"],
                },
              }),
            },
            !isValueInvalid ? "scheduleOffsetDays" : undefined
          );
        } else if (!isValueInvalid) {
          edit.deleteError("scheduleOffsetDays");
        }
      },
    },
  ];

  edit.setValidation(fields);

  const handleModalClose = () => {
    onClose();
    edit.reset();
  };

  const onAddTemplate = () => {
    if (item) {
      onSubmit({ name: item.name, modifiedOn: item.modifiedOn, ...edit.edits });
      handleModalClose();
      return;
    }

    const { id: assignmentTemplateId } = findTemplate(edit.getValue("name"));

    if (assignmentTemplateId) {
      onSubmit({
        assignmentTemplateId,
        ...edit.getValue(),
      });
    }

    handleModalClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: buttonTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: onAddTemplate,
          disabled: loading || !edit.isValid(),
        },
      ]}
      title={title}
      topRightClose={handleModalClose}
      loading={loading}
    >
      <MiDetailFields detailFields={fields} edit={edit} />
    </MiModal>
  );
}
